'use client'

import { format, isSameMonth, isToday } from 'date-fns'
import { Button } from '@/shared/components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import type { WeekSpanSegment } from '@/features/volunteers/components/month-span-calendar/month-span-calendar-utils'

export const MONTH_SPAN_WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export type MonthSpanDayCellRenderArgs<T = unknown> = {
  day: Date
  dayCol: number
  weekDays: Date[]
  weekIdx: number
  isCurrentMonth: boolean
  isTodayDate: boolean
  /** Segments for this Sun–Sat week (computed once per week) */
  weekSegments: WeekSpanSegment<T>[]
}

export interface MonthSpanCalendarProps<T = unknown> {
  currentMonth: Date
  onPrevMonth: () => void
  onNextMonth: () => void
  onToday: () => void
  /** Sun-start week rows covering the month grid */
  weeks: Date[][]
  monthTitleId?: string
  /** Called once per week — reuse result in day cells + bar row */
  getSegmentsForWeek: (weekStart: Date) => WeekSpanSegment<T>[]
  renderDayCell: (args: MonthSpanDayCellRenderArgs<T>) => React.ReactNode
  /** Bars row for each week (typically a grid with colspan segments) */
  renderWeekBarRow: (weekStart: Date, weekIdx: number, weekSegments: WeekSpanSegment<T>[]) => React.ReactNode
  /** Attach to the week row that contains today (e.g. scroll into view) */
  todayWeekRowRef?: React.Ref<HTMLDivElement>
  /** Loading skeleton replaces grid */
  isLoading?: boolean
  /** Min height + overflow for the trip bars area per week (default: grows with stacked bars; no inner vertical scroll) */
  weekBarScrollClassName?: string
  /** When false, only the weekday row + week rows render (use an external month title + nav). */
  showMonthToolbar?: boolean
  /** Merged onto the root wrapper */
  className?: string
  /** Use ReliefHub primary (teal) for outer border and grid lines — matches FAB / brand accent */
  brandOutline?: boolean
}

export function MonthSpanCalendar<T = unknown>({
  currentMonth,
  onPrevMonth,
  onNextMonth,
  onToday,
  weeks,
  monthTitleId,
  getSegmentsForWeek,
  renderDayCell,
  renderWeekBarRow,
  todayWeekRowRef,
  isLoading,
  weekBarScrollClassName =
    'min-h-[6rem] sm:min-h-[7rem] lg:min-h-[8rem] overflow-x-hidden overflow-y-visible',
  showMonthToolbar = true,
  className,
  brandOutline = false,
}: MonthSpanCalendarProps<T>) {
  return (
    <div
      className={cn(
        'rounded-xl border bg-card text-card-foreground overflow-hidden',
        brandOutline
          ? 'border-2 border-primary shadow-sm ring-1 ring-primary/20'
          : 'border',
        className
      )}
    >
      {showMonthToolbar && (
        <div
          className={cn(
            'flex flex-col gap-3 border-b bg-muted/20 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-4',
            brandOutline && 'border-primary/45'
          )}
        >
          <h3 id={monthTitleId} className="text-base font-semibold tabular-nums">
            {format(currentMonth, 'MMMM yyyy')}
          </h3>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-xl bg-muted/50 p-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onPrevMonth}
                className="h-9 w-9 rounded-lg"
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onNextMonth}
                className="h-9 w-9 rounded-lg"
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={onToday} className="rounded-lg font-medium">
              Today
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="h-[400px] animate-pulse bg-muted/30" aria-hidden />
      ) : (
        <>
          <div
            className={cn(
              'grid grid-cols-[repeat(7,minmax(0,1fr))] border-b-2 bg-muted/30',
              brandOutline ? 'border-primary/50 bg-primary/5' : 'border-muted-foreground/30'
            )}
          >
            {MONTH_SPAN_WEEKDAY_LABELS.map((label) => (
              <div
                key={label}
                className={cn(
                  'px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider',
                  brandOutline ? 'text-primary' : 'text-muted-foreground'
                )}
              >
                {label}
              </div>
            ))}
          </div>

          <div>
            {weeks.map((weekDays, weekIdx) => {
              const weekStart = weekDays[0]
              const weekSegments = getSegmentsForWeek(weekStart)
              const weekContainsToday = weekDays.some((d) => isToday(d))
              return (
                <div
                  key={weekStart.toISOString()}
                  ref={weekContainsToday ? todayWeekRowRef : undefined}
                  className={cn(
                    'border-b-2 last:border-b-0',
                    brandOutline ? 'border-primary/35' : 'border-muted-foreground/30',
                    weekIdx % 2 === 1 && (brandOutline ? 'bg-primary/[0.03]' : 'bg-muted/10')
                  )}
                >
                  <div className="grid min-h-[48px] grid-cols-[repeat(7,minmax(0,1fr))] sm:min-h-[40px] lg:min-h-[2.75rem] items-stretch">
                    {weekDays.map((day, dayCol) => {
                      const isCurrentMonth = isSameMonth(day, currentMonth)
                      const isTodayDate = isToday(day)
                      return (
                        <div key={day.toISOString()} className="flex min-h-0 min-w-0 overflow-x-clip">
                          {renderDayCell({
                            day,
                            dayCol,
                            weekDays,
                            weekIdx,
                            isCurrentMonth,
                            isTodayDate,
                            weekSegments,
                          })}
                        </div>
                      )
                    })}
                  </div>
                  <div
                    className={cn(
                      'grid grid-cols-[repeat(7,minmax(0,1fr))] content-start items-start gap-y-1 px-0.5 pb-2',
                      weekBarScrollClassName
                    )}
                    style={{ gridAutoRows: 'minmax(28px, max-content)' }}
                  >
                    {renderWeekBarRow(weekStart, weekIdx, weekSegments)}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
